/**
 * Full-page booking form (to-fill). Opened from My Bookings when user clicks a "to fill" card.
 * Submitted forms are shown in sheet only from JobsPage.
 * iOS-first UI with project-consistent Checkbox, Input, and section cards.
 */

import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  ChevronDown,
  ChevronUp,
  Info,
  User,
  CreditCard,
  FileText,
  Banknote,
  Percent,
  Wallet,
  CalendarClock,
} from "lucide-react";
import toast from "react-hot-toast";
import apiClient from "../../../api/client";
import { API_ENDPOINTS } from "../../../api/endpoints";
import type { BookingFormItem, BookingFormSubmitPayload } from "../JobsPage";
import { usePerformanceDetailsStore } from "../../../stores/performanceDetailsStore";
import { useTechRiderTblStore } from "../../../stores/techRiderTblStore";
import { useBillingStore } from "../../../stores/billingStore";
import { useBookingFormsStore } from "../../../stores/bookingFormsStore";
import { Button } from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";
import { Input } from "../../../components/ui/Input";
import { Loader } from "@/components/ui";
import { AnnexureAContent } from "@/components/booking/AnnexureAContent";
import { cn } from "@/utils/cn";

function formatBookingEventDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const day = d.getDate();
  const mon = d.toLocaleDateString("en-IN", { month: "short" });
  const year = d.getFullYear();
  return `${day} ${mon} ${year}`;
}

function formatBookingINR(value: string | number | null | undefined): string {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `₹${n.toLocaleString("en-IN")}`;
}

function removeSecondsFromTime(timeStr: string | null | undefined): string {
  if (!timeStr) return "—";
  const match = timeStr.match(/^(\d{1,2}:\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (match) return `${match[1]} ${(match[2] ?? "").toUpperCase()}`.trim();
  return timeStr;
}

function formatPaymentTerms(terms: string | null | undefined): string {
  if (!terms) return "—";
  const t = String(terms).trim();
  const n = parseInt(t, 10);
  if (!Number.isNaN(n) && String(n) === t)
    return `Within ${n} day${n !== 1 ? "s" : ""}`;
  return t;
}

type ExpressBookingDetailsResponse = {
  success: boolean;
  data?: {
    pipedrive_deal_id?: number;
    express_booking_id?: number;
    booking_id?: string;
    artist_details?: {
      professional_name?: string;
      email?: string;
      phone?: string;
    };
    billing_details?: {
      pan_details?: unknown;
      banks?: unknown[];
    };
    event_details?: {
      event_type?: string;
      event_date?: string;
      event_time?: string;
      event_duration?: number;
      venue_type?: string | null;
      gathering_size?: string;
    };
    location?: {
      address?: string;
      city?: string;
      state?: string;
      pin_code?: string;
      latitude?: number;
      longitude?: number;
      map_link?: string;
    };
    fee_and_payment?: {
      base_price?: number;
      taf_percent?: number;
      taf_amount?: number;
      final_balance?: number;
      payment_terms?: string;
      is_sound_setup_added?: boolean;
      sound_setup_price?: number;
    };
    offer?: {
      is_offer_applied?: boolean;
      offer_id?: number | null;
      offer_title?: string | null;
      offer_kind?: string | null;
      discount_percent?: number | null;
      discount_amount?: number | null;
      snapshot?: unknown;
    };
    performing_members?: {
      count?: number;
      label?: string;
    };
  };
};

type ExpressBookingSubmitPayload = {
  bank_id: number;
  pan: string;
};

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function subtractMinutesFromTimeString(
  timeStr: string | null | undefined,
  minutesToSubtract: number,
): string | null {
  if (!timeStr) return null;
  const raw = String(timeStr).trim();
  const m = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  const ss = Number(m[3] ?? "0");
  if (![hh, mm, ss].every((v) => Number.isFinite(v))) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59 || ss < 0 || ss > 59) return null;

  const daySeconds = 24 * 60 * 60;
  const total = hh * 3600 + mm * 60 + ss;
  const next = ((total - minutesToSubtract * 60) % daySeconds + daySeconds) % daySeconds;

  const outH = Math.floor(next / 3600);
  const outM = Math.floor((next % 3600) / 60);
  const outS = next % 60;
  return `${pad2(outH)}:${pad2(outM)}:${pad2(outS)}`;
}

function formatINRFromNumber(n: number | null | undefined): string {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return `₹${num.toLocaleString("en-IN")}`;
}

function InlineInfoTooltip({ text }: { text: string }) {
  return (
    <button
      type="button"
      className="group relative inline-flex items-center justify-center h-5 w-5 -ml-1 rounded-full text-[#8E8E93] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363]/60"
      aria-label="Info"
    >
      <Info className="h-3.5 w-3.5" />
      <span className="pointer-events-none absolute left-full top-1/2 z-10 ml-2 -translate-y-1/2 w-max max-w-[220px] rounded-xl border border-[#3A3A3C] bg-[#1C1C1E] px-3 py-2 text-[12px] leading-snug text-[#F2F2F7] opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100">
        {text}
      </span>
    </button>
  );
}

const REGEX_GST = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const REGEX_IFSC = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const REGEX_ACCOUNT = /^\d{9,18}$/;
const REGEX_PAN = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

function extractBookingFormsPayload(payload: unknown): BookingFormItem[] {
  // Staging/prod can return either:
  // - { success: true, data: BookingFormItem[] }
  // - { success: true, data: { results: BookingFormItem[] } }
  if (!payload || typeof payload !== "object") return [];
  const data = (payload as { data?: unknown }).data;
  if (Array.isArray(data)) return data as BookingFormItem[];
  if (data && typeof data === "object") {
    const results = (data as { results?: unknown }).results;
    if (Array.isArray(results)) return results as BookingFormItem[];
  }
  return [];
}

export const BookingFillPage: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const formFromState = (location.state as { form?: BookingFormItem })?.form;
  const expressHydratedRef = useRef(false);
  const bookingFormsRetryCountRef = useRef(0);
  const [expressSoundSetupAdded, setExpressSoundSetupAdded] = useState<
    boolean | null
  >(null);
  const [expressOfferKind, setExpressOfferKind] = useState<string | null>(null);

  const [form, setForm] = useState<BookingFormItem | null>(
    formFromState ?? null,
  );
  const [formLoading, setFormLoading] = useState(!formFromState);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [preConfirmModalOpen, setPreConfirmModalOpen] = useState(false);
  const [annexureOpen, setAnnexureOpen] = useState(false);

  const ensurePerformanceFetched = usePerformanceDetailsStore(
    (s) => s.ensureFetched,
  );
  const techRiderTblData = useTechRiderTblStore((s) => s.data);
  const ensureTechRiderTblFetched = useTechRiderTblStore(
    (s) => s.ensureFetched,
  );
  const billingData = useBillingStore((s) => s.data);
  const ensureBillingFetched = useBillingStore((s) => s.ensureFetched);

  const membersCount = form?.members_count ?? 0;
  const techRiderEnabled = String(form?.tech_rider ?? "") === "1";
  const tblEnabled = Number(form?.tbl_needed) === 1;

  /*
   * Performing members selection + add-member flow
   * ------------------------------------------------
   * The full UI and logic for selecting/adding performing members exists below,
   * but is currently commented out in the JSX section per product requirements.
   * Do not delete it; keep it available for future enablement.
   *
   * When enabling, restore (uncomment) the related state + handlers:
   *
   * const performanceData = usePerformanceDetailsStore((s) => s.data);
   * const updatePerformanceDetails = usePerformanceDetailsStore((s) => s.updatePerformanceDetails);
   * const saveLoadingPerformance = usePerformanceDetailsStore((s) => s.saveLoading);
   *
   * const profileMembers: MemberOption[] = (performanceData?.performing_members ?? []).map(
   *   (m: PerformanceMemberItem, i: number) => ({
   *     id: `profile-${m.id ?? i}`,
   *     name: m.name ?? "",
   *     role: m.role ?? "",
   *     age: m.age ?? 0,
   *   }),
   * );
   * const allOptions: MemberOption[] = profileMembers;
   * const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
   *
   * useEffect(() => {
   *   const validIds = new Set(allOptions.map((o) => o.id));
   *   setSelectedMemberIds((prev) => {
   *     let changed = false;
   *     const next = new Set<string>();
   *     prev.forEach((id) => {
   *       if (validIds.has(id)) next.add(id);
   *       else changed = true;
   *     });
   *     return changed ? next : prev;
   *   });
   * }, [performanceData?.performing_members]);
   *
   * const [showAddMemberForm, setShowAddMemberForm] = useState(false);
   * const [draftNewMembers, setDraftNewMembers] = useState<DraftMemberRow[]>(() => [
   *   { id: `draft-${Date.now()}`, name: "", role: "", age: "", gender: "" },
   * ]);
   * const [addMemberErrors, setAddMemberErrors] = useState<Record<string, string>>({});
   *
   * const handleToggleMember = (id: string, checked: boolean) => { ... };
   * const handleOpenAddMember = () => { ... };
   * const handleAddMoreDraftMembers = () => { ... };
   * const handleRemoveDraftMember = (id: string) => { ... };
   * const handleDraftMemberChange = (...) => { ... };
   * const handleSaveAddMembers = async () => { ... };
   * const handleCancelAddMembers = () => { ... };
   *
   * And in validate()/submit:
   * - require selecting `membersCount` members
   * - build `members_info` from the selected members
   */

  const [techRiderValue, setTechRiderValue] = useState("");
  const [travelBoardingLodging, setTravelBoardingLodging] = useState("");
  const [selectedBankId, setSelectedBankId] = useState<number | null>(null);
  const [gstin, setGstin] = useState("");
  const [pan, setPan] = useState("");
  const [panError, setPanError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [preConfirmChecks, setPreConfirmChecks] = useState({
    report15MinEarly: false,
    soundSetupIncluded: false,
    bringInstrumentalist: false,
    collectOtp: false,
  });

  const [expressPerformingMembers, setExpressPerformingMembers] = useState<{
    count: number | null;
    label: string;
  }>({ count: null, label: "" });

  const bankFieldsFromSelection =
    selectedBankId != null
      ? billingData?.banking_details?.find((b) => b.id === selectedBankId)
      : null;

  const bank_name = bankFieldsFromSelection?.bank_name ?? "";
  const branch_name = bankFieldsFromSelection?.branch ?? "";
  const ifsc_code = bankFieldsFromSelection?.ifsc_code ?? "";
  const account_number = bankFieldsFromSelection?.account_number ?? "";
  const account_name = bankFieldsFromSelection?.account_holder_name ?? "";
  const account_type =
    bankFieldsFromSelection?.account_type === "current" ? "Current" : "Savings";

  useEffect(() => {
    bookingFormsRetryCountRef.current = 0;
    expressHydratedRef.current = false;
  }, [formId]);

  useEffect(() => {
    if (!formId) return;
    const numId = Number(formId);

    if (formFromState) {
      // Express flow passes a synthetic form via location.state where `deal_id` matches `formId`.
      // Treat that as authoritative so we don't overwrite it with null and start polling.
      const matchesRoute =
        (Number.isFinite(numId) && formFromState.id === numId) ||
        (Number.isFinite(numId) && formFromState.deal_id === numId);
      if (matchesRoute) {
        setForm(formFromState);
        setFormLoading(false);
        return;
      }
    }

    setFormLoading(true);
    apiClient
      .get(API_ENDPOINTS.AUTH.ARTIST_BOOKING_FORMS)
      .then((res) => {
        if (res.data?.success) {
          const list = extractBookingFormsPayload(res.data);
          const numId = Number(formId);
          const found =
            list.find((f) => f.id === numId) ??
            list.find((f) => f.deal_id === numId);
          setForm(found ?? null);
        } else {
          setForm(null);
        }
      })
      .catch(() => setForm(null))
      .finally(() => setFormLoading(false));
  }, [formId, formFromState?.id, formFromState?.deal_id]);

  // Express flow: when opened from Gigs (Apply), JobsPage passes a synthetic form (mostly empty).
  // Hydrate this page using GET jobs/express-booking/:dealId/ and merge fields into the existing
  // BookingFillPage state without changing the UI.
  useEffect(() => {
    if (!formId) return;
    if (!form) return;

    const dealId = Number(formId);
    if (!Number.isFinite(dealId)) return;
    if (expressHydratedRef.current) return;

    const looksSynthetic =
      (form.contact_phone ?? "").trim() === "" ||
      (form.contact_email ?? "").trim() === "" ||
      (form.event_date ?? "").trim() === "" ||
      (form.venue ?? "").trim() === "" ||
      (form.payment_terms ?? "").trim() === "" ||
      (form.fee ?? "").trim() === "" ||
      (form.venue_city_state ?? "").trim() === "";

    if (!looksSynthetic) return;

    expressHydratedRef.current = true;

    apiClient
      .get<ExpressBookingDetailsResponse>(
        API_ENDPOINTS.AUTH.JOBS_EXPRESS_BOOKING_DETAIL(dealId),
      )
      .then((res) => {
        const payload = res.data;
        if (!payload?.success || !payload.data) return;
        const d = payload.data;
        setExpressSoundSetupAdded(
          d.fee_and_payment?.is_sound_setup_added === true,
        );
        setExpressOfferKind(d.offer?.offer_kind ?? null);
        setExpressPerformingMembers({
          count:
            typeof d.performing_members?.count === "number"
              ? d.performing_members.count
              : null,
          label: String(d.performing_members?.label ?? ""),
        });

        const startTime = (d.event_details?.event_time ?? "").trim();
        const reportTime =
          subtractMinutesFromTimeString(startTime, 15) ?? "";

        const address = (d.location?.address ?? "").trim();
        const city = (d.location?.city ?? "").trim();
        const state = (d.location?.state ?? "").trim();
        const cityState =
          [city, state].filter(Boolean).join(", ").trim() || "";

        const mapped: Partial<BookingFormItem> = {
          id: form.id,
          deal_id: form.deal_id,
          contact_name: d.artist_details?.professional_name ?? "",
          contact_phone: d.artist_details?.phone ?? "",
          contact_email: d.artist_details?.email ?? "",
          event_type: d.event_details?.event_type ?? "",
          event_date: d.event_details?.event_date ?? "",
          event_start_at: startTime || null,
          report_at: reportTime,
          performance_duration_mins: Number(
            d.event_details?.event_duration ?? 0,
          ),
          venue: address,
          venue_city_state: cityState,
          pin_code: d.location?.pin_code ?? null,
          google_map_link: d.location?.map_link ?? null,
          fee: formatINRFromNumber(d.fee_and_payment?.base_price),
          advance_percentage:
            d.fee_and_payment?.taf_percent != null
              ? String(d.fee_and_payment.taf_percent)
              : "",
          advance_amount: formatINRFromNumber(d.fee_and_payment?.taf_amount),
          balance_amount: formatINRFromNumber(d.fee_and_payment?.final_balance),
          payment_terms: d.fee_and_payment?.payment_terms ?? "",
        };

        const pickIfEmpty = (prevVal: string, nextVal: string) => {
          const p = (prevVal ?? "").trim();
          const n = (nextVal ?? "").trim();
          return p ? prevVal : n;
        };

        setForm((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            // Contact fields are display-only on this page; prefer Express API values if present.
            contact_name: (mapped.contact_name ?? "").trim()
              ? (mapped.contact_name as string)
              : prev.contact_name,
            contact_phone: (mapped.contact_phone ?? "").trim()
              ? (mapped.contact_phone as string)
              : prev.contact_phone,
            contact_email: (mapped.contact_email ?? "").trim()
              ? (mapped.contact_email as string)
              : prev.contact_email,
            event_type: pickIfEmpty(prev.event_type, mapped.event_type ?? ""),
            event_date: pickIfEmpty(prev.event_date, mapped.event_date ?? ""),
            report_at: pickIfEmpty(prev.report_at, mapped.report_at ?? ""),
            event_start_at:
              prev.event_start_at && String(prev.event_start_at).trim()
                ? prev.event_start_at
                : (mapped.event_start_at ?? prev.event_start_at),
            performance_duration_mins:
              Number(prev.performance_duration_mins ?? 0) > 0
                ? prev.performance_duration_mins
                : Number(mapped.performance_duration_mins ?? prev.performance_duration_mins),
            venue: pickIfEmpty(prev.venue, mapped.venue ?? ""),
            venue_city_state: pickIfEmpty(prev.venue_city_state, mapped.venue_city_state ?? ""),
            pin_code: prev.pin_code ? prev.pin_code : (mapped.pin_code ?? prev.pin_code),
            google_map_link:
              prev.google_map_link && String(prev.google_map_link).trim()
                ? prev.google_map_link
                : (mapped.google_map_link ?? prev.google_map_link),
            // Fee & payment fields are read-only on this page; always prefer Express API values
            // even if synthetic form provided budget placeholders.
            fee: (mapped.fee ?? "").trim() ? (mapped.fee as string) : prev.fee,
            advance_percentage: (mapped.advance_percentage ?? "").trim()
              ? (mapped.advance_percentage as string)
              : prev.advance_percentage,
            advance_amount: (mapped.advance_amount ?? "").trim()
              ? (mapped.advance_amount as string)
              : prev.advance_amount,
            balance_amount: (mapped.balance_amount ?? "").trim()
              ? (mapped.balance_amount as string)
              : prev.balance_amount,
            payment_terms: (mapped.payment_terms ?? "").trim()
              ? (mapped.payment_terms as string)
              : prev.payment_terms,
          };
        });
      })
      .catch(() => {
        // keep existing behavior if express hydration fails
      });
  }, [formId, form?.id]);

  // If form isn't available yet (Express flow), keep using the existing BookingFillPage UI.
  // We'll auto-retry fetching booking-forms until it appears, instead of redirecting away
  // or showing a new placeholder UI.
  useEffect(() => {
    if (!formId) return;
    if (formLoading) return;
    if (form) return;
    if (formFromState) return;

    const numId = Number(formId);
    if (!Number.isFinite(numId)) return;

    const interval = window.setInterval(() => {
      bookingFormsRetryCountRef.current += 1;
      if (bookingFormsRetryCountRef.current > 10) {
        window.clearInterval(interval);
        return;
      }
      apiClient
        .get(API_ENDPOINTS.AUTH.ARTIST_BOOKING_FORMS)
        .then((res) => {
          if (res.data?.success) {
            const list = extractBookingFormsPayload(res.data);
            const found =
              list.find((f) => f.id === numId) ??
              list.find((f) => f.deal_id === numId);
            if (found) {
              setForm(found);
              setFormLoading(false);
              window.clearInterval(interval);
            }
          }
        })
        .catch(() => {
          // ignore; keep retrying
        });
    }, 2000);

    return () => window.clearInterval(interval);
  }, [formId, formLoading, form, formFromState]);

  useEffect(() => {
    ensurePerformanceFetched();
    ensureTechRiderTblFetched();
    ensureBillingFetched();
  }, [
    ensurePerformanceFetched,
    ensureTechRiderTblFetched,
    ensureBillingFetched,
  ]);

  useEffect(() => {
    if (!form) return;
    setTechRiderValue(form.tech_rider_value ?? "");
    setTravelBoardingLodging(form.travel_boarding_lodging ?? "");
    setGstin((form.gstin ?? "").trim());
    const nextPan = (form.pan ?? "").trim().toUpperCase();
    setPan(nextPan);
    if (nextPan && !REGEX_PAN.test(nextPan)) setPanError("Invalid PAN format");
    else setPanError("");
    const banks = billingData?.banking_details ?? [];
    const match = banks.find(
      (b) =>
        b.account_number === form.account_number &&
        b.ifsc_code === form.ifsc_code,
    );
    // Prefer matching bank from form; otherwise auto-select default account if available.
    const defaultBank = banks.find((b) => (b as { default?: boolean })?.default);
    setSelectedBankId(match?.id ?? defaultBank?.id ?? null);
  }, [form?.id, billingData?.banking_details]);

  // If banking details load after the form (common), auto-select the default account.
  useEffect(() => {
    const banks = billingData?.banking_details ?? [];
    if (banks.length === 0) return;
    if (selectedBankId != null) return;
    const defaultBank = banks.find((b) => (b as { default?: boolean })?.default);
    if (defaultBank?.id != null) setSelectedBankId(defaultBank.id);
  }, [billingData?.banking_details, selectedBankId]);

  useEffect(() => {
    if (!techRiderTblData || !form) return;
    if (techRiderEnabled) {
      if (
        techRiderTblData.tech_rider &&
        (techRiderTblData.tech_rider_desc ?? "").trim()
      ) {
        setTechRiderValue(techRiderTblData.tech_rider_desc ?? "");
      } else {
        setTechRiderValue("");
      }
    }
    if (tblEnabled) {
      if (techRiderTblData.tbl && (techRiderTblData.tbl_desc ?? "").trim()) {
        setTravelBoardingLodging(techRiderTblData.tbl_desc ?? "");
      } else {
        setTravelBoardingLodging("");
      }
    }
  }, [
    form?.id,
    techRiderEnabled,
    tblEnabled,
    techRiderTblData?.tech_rider,
    techRiderTblData?.tech_rider_desc,
    techRiderTblData?.tbl,
    techRiderTblData?.tbl_desc,
  ]);

  useEffect(() => {
    if (!billingData?.gst_details) return;
    setGstin(billingData.gst_details.gstin ?? "");
    const nextPan = (billingData.gst_details.pan ?? "").toUpperCase();
    setPan(nextPan);
    if (nextPan && !REGEX_PAN.test(nextPan)) setPanError("Invalid PAN format");
    else setPanError("");
  }, [billingData?.gst_details?.gstin, billingData?.gst_details?.pan]);

  const validate = (): string | null => {
    if (!form) return "Form not loaded.";
    if (techRiderEnabled && !techRiderValue.trim())
      return "Tech rider details are required.";
    if (tblEnabled && !travelBoardingLodging.trim())
      return "Travel / boarding / lodging details are required.";
    if (selectedBankId == null) return "Please select a bank account.";
    const bank = billingData?.banking_details?.find(
      (b) => b.id === selectedBankId,
    );
    if (!bank) return "Please select a bank account.";
    if (!REGEX_IFSC.test(bank.ifsc_code.trim()))
      return "Invalid IFSC code on selected account.";
    if (!REGEX_ACCOUNT.test(bank.account_number.trim()))
      return "Invalid account number on selected account.";
    if (gstin.trim() && !REGEX_GST.test(gstin.trim()))
      return "Enter a valid GSTIN.";
    if (!pan.trim()) return "PAN number is required.";
    if (pan.trim() && !REGEX_PAN.test(pan.trim().toUpperCase()))
      return "Invalid PAN format.";
    if (!termsAccepted)
      return "You must accept the terms and conditions to proceed.";
    return null;
  };

  const handleConfirmBookingClick = () => {
    const err = validate();
    setValidationError(err ?? null);
    if (err) {
      toast.error(err);
      return;
    }
    setPreConfirmModalOpen(true);
  };

  // Enable proceed only when ALL visible checkboxes are checked.
  // If some checkboxes are conditionally hidden (sound setup / offer), they are not required.
  const isPreConfirmComplete =
    preConfirmChecks.report15MinEarly &&
    preConfirmChecks.collectOtp &&
    (expressSoundSetupAdded === true ? preConfirmChecks.soundSetupIncluded : true) &&
    (expressOfferKind != null ? preConfirmChecks.bringInstrumentalist : true);

  const handlePreConfirmProceed = () => {
    if (!isPreConfirmComplete) {
      toast.error("Please confirm all required points to proceed.");
      return;
    }
    setPreConfirmModalOpen(false);
    setConfirmModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!form) return;
    const err = validate();
    if (err) {
      setValidationError(err);
      toast.error(err);
      setConfirmModalOpen(false);
      return;
    }
    setValidationError(null);
    setSubmitLoading(true);
    try {
      const dealId = Number(formId);
      const isExpress = Number.isFinite(dealId) && form.deal_id === dealId;
      if (isExpress) {
        const bankId = selectedBankId;
        if (bankId == null) {
          toast.error("Please select a bank account.");
          return;
        }
        const expressPayload: ExpressBookingSubmitPayload = {
          bank_id: bankId,
          pan: pan.trim().toUpperCase(),
        };
        const res = await apiClient.post(
          API_ENDPOINTS.AUTH.JOBS_EXPRESS_BOOKING_DETAIL(dealId),
          expressPayload,
        );
        const data = (res as any)?.data;
        if (data?.success === false) {
          const apiMsg =
            (typeof data?.error?.message === "string" ? data.error.message : undefined) ||
            (typeof data?.message === "string" ? data.message : undefined) ||
            "Something went wrong. Please try again.";
          setValidationError(apiMsg);
          toast.error(apiMsg);
          return;
        }
      } else {
        const members_info = JSON.stringify([]);
        const payload: BookingFormSubmitPayload = {
          members_info,
          tech_rider_value: techRiderEnabled ? techRiderValue.trim() : "",
          travel_boarding_lodging: tblEnabled ? travelBoardingLodging.trim() : "",
          gstin: gstin.trim(),
          pan: pan.trim().toUpperCase(),
          bank_name,
          branch_name,
          ifsc_code,
          account_number,
          account_name,
          account_type: account_type === "Current" ? "Current" : "Savings",
          feedback: "",
          rating: 0,
        };
        await apiClient.post(
          API_ENDPOINTS.AUTH.ARTIST_BOOKING_FORM_SUBMIT(form.id),
          payload,
        );
      }
      toast.success("Booking confirmed successfully.");
      setConfirmModalOpen(false);
      await useBookingFormsStore.getState().fetchBookingForms();
      navigate("/jobs", { state: { tab: "express" } });
    } catch (err: any) {
      const data = err?.response?.data;
      const apiMsg =
        (typeof data?.error?.message === "string" ? data.error.message : undefined) ||
        (typeof data?.message === "string" ? data.message : undefined) ||
        (typeof err?.message === "string" ? err.message : undefined) ||
        "Something went wrong. Please try again.";
      setValidationError(apiMsg);
      toast.error(apiMsg);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (formLoading || !form) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center">
        <Loader size="small" />
      </div>
    );
  }

  const banks = billingData?.banking_details ?? [];
  const gstDetails = billingData?.gst_details;
  const hasPan = (gstDetails?.pan ?? "").trim().length > 0;

  const inputBaseClass =
    "w-full rounded-[0.75rem] bg-transparent px-4 py-3 text-sm text-white placeholder:text-[#8E8E93] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] border border-[#3A3A3C] min-h-[44px]";

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white pb-28">
      {/* Header — minimal */}
      <header className="bg-[#0B0B0D] border-b border-[#3A3A3C] safe-area-inset-top">
        <div className="flex items-center gap-3 px-4 h-14">
          <button
            type="button"
            onClick={() => navigate("/jobs", { state: { tab: "express" } })}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center -ml-2 rounded-xl text-[#8E8E93] hover:text-white hover:bg-[#1C1C1E] active:scale-[0.98] transition-colors"
            aria-label="Back to My Bookings"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex flex-col min-w-0">
            <h1 className="text-[17px] font-semibold text-white truncate">
              {(form.contact_name ?? "").trim() ? form.contact_name : form.artist_act}
            </h1>
            {/* <p className="text-[13px] text-[#8E8E93] truncate">{form.event_type} · #{form.id}</p> */}
            <p className="text-[13px] text-[#8E8E93] truncate">
              {form.contact_phone} · {form.contact_email}
            </p>
          </div>
        </div>
      </header>

      <div className="md:px-4 py-10  mx-auto space-y-6">
        {/* Inline validation error — no toasts */}
        {validationError && (
          <div
            role="alert"
            className="flex items-start gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
          >
            <span className="shrink-0 mt-0.5">!</span>
            <p className="flex-1">{validationError}</p>
            <button
              type="button"
              onClick={() => setValidationError(null)}
              className="shrink-0 text-red-400/80 hover:text-red-400"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Contact & event — minimal; on md+ Contact | When & where in row, Fee & payment right below When & where */}
        <section className="px-1 py-3 text-[13px] grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-x-6">
          {/* <div className="space-y-1.5">
            <p className="text-[11px] font-medium text-[#6E6E73] uppercase tracking-wider">Contact</p>
            <p className="text-white/90 font-medium flex items-center gap-2">
              <User className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
              {form.contact_name}
            </p>
            <p className="text-[#8E8E93] flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
              {form.contact_phone}
            </p>
            <p className="text-[#8E8E93] flex items-center gap-2 truncate">
              <Mail className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
              {form.contact_email}
            </p>
          </div> */}
          <div className="space-y-1.5 pt-2 md:pt-0 border-t border-[#3A3A3C]/50 md:border-t-0">
            <p className="text-[11px] font-medium text-[#6E6E73] uppercase tracking-wider">
              Event Details
            </p>
            <p className="text-[#8E8E93] flex items-center gap-1.5">
              {/* <Calendar className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" /> */}
              {form.event_type} · {formatBookingEventDate(form.event_date)} ·
              Report {removeSecondsFromTime(form.report_at)} · Start{" "}
              {removeSecondsFromTime(form.event_start_at)}
              {form.performance_duration_mins != null &&
                ` · ${form.performance_duration_mins} min`}
            </p>
            <p className="text-white/90 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
              {form.venue}
              {form.venue_city_state && form.venue_city_state !== form.venue
                ? ` · ${form.venue_city_state}`
                : ""}
            </p>
            {form.google_map_link && (
              <a
                href={form.google_map_link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#F33363]/90 hover:bg-[#F33363]/70 text-white hover:text-white/80 font-medium text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 w-fit"
              >
                <MapPin className="h-3.5 w-3.5" /> Map
              </a>
            )}
          </div>

          <div className="space-y-1.5 pt-4 border-t border-[#3A3A3C]/50 block md:invisible">
            <p className="text-[11px] font-medium text-[#6E6E73] uppercase tracking-wider">
              Fee & payment
            </p>
            <div className="md:col-start-2 flex flex-col space-y-1.5 md:space-y-1">
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className="flex flex-row items-center gap-2">
                  <Banknote className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p>
                    Fee
                    {expressSoundSetupAdded === true
                      ? " (incl. Sound Setup)"
                      : ""}
                  </p>
                </div>

                {formatBookingINR(form.fee)}
              </p>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className="flex flex-row items-center gap-2">
                  <Percent className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p className="inline-flex items-center gap-1.5">
                    TAF{" "}
                    {form.advance_percentage != null &&
                    form.advance_percentage !== ""
                      ? `(${String(form.advance_percentage).replace(/\.00$/, "")}%)`
                      : "—"}
                    <InlineInfoTooltip text="Platform Fee" />
                  </p>
                </div>
                {formatBookingINR(form.advance_amount)}
              </p>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className="flex flex-row items-center gap-2">
                  <Wallet className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p className="inline-flex items-center gap-1.5">
                    Balance <InlineInfoTooltip text="Your Fee" />
                  </p>
                </div>
                {formatBookingINR(form.balance_amount)}
              </p>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className="flex flex-row items-center gap-2">
                  <CalendarClock className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p className="inline-flex items-center gap-1.5">
                    Payment Terms <InlineInfoTooltip text="Payable" />
                  </p>
                </div>
                {formatPaymentTerms(form.payment_terms)}
              </p>
            </div>
          </div>
          <div className="space-y-1.5 pt-4 border-t border-[#3A3A3C]/50 hidden md:block">
            <p className="text-[11px] font-medium text-[#6E6E73] uppercase tracking-wider">
              Fee & payment
            </p>
            <div className="md:col-start-2 flex flex-col space-y-1.5 md:space-y-1">
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className="flex flex-row items-center gap-2">
                  <Banknote className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p>
                    Fee
                    {expressSoundSetupAdded === true
                      ? " (incl. Sound Setup)"
                      : ""}
                  </p>
                </div>

                {formatBookingINR(form.fee)}
              </p>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className="flex flex-row items-center gap-2">
                  <Percent className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p className="inline-flex items-center gap-1.5">
                    TAF{" "}
                    {form.advance_percentage != null &&
                    form.advance_percentage !== ""
                      ? `(${String(form.advance_percentage).replace(/\.00$/, "")}%)`
                      : "—"}
                    <InlineInfoTooltip text="Platform Fee" />
                  </p>
                </div>
                {formatBookingINR(form.advance_amount)}
              </p>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className="flex flex-row items-center gap-2">
                  <Wallet className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p className="inline-flex items-center gap-1.5">
                    Balance <InlineInfoTooltip text="Your Fee" />
                  </p>
                </div>
                {formatBookingINR(form.balance_amount)}
              </p>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className="flex flex-row items-center gap-2">
                  <CalendarClock className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p className="inline-flex items-center gap-1.5">
                    Payment Terms <InlineInfoTooltip text="Payable" />
                  </p>
                </div>
                {formatPaymentTerms(form.payment_terms)}
              </p>
            </div>
          </div>
        </section>

        {/* Performing members */}
        <section className="rounded-[2rem] bg-[#151517] border border-[#2E2E32] overflow-hidden">
          <div className="p-5 border-b border-[#2E2E32]">
            <h2 className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider flex items-center gap-2">
              <User className="h-4 w-4" /> Performing members
            </h2>
            {/* <p className="text-white/80 text-sm mt-1">
              Select up to {membersCount} from your profile. Add members below
              to save to profile, then select here.
            </p> */}
          </div>

          {(expressPerformingMembers.count != null ||
            (expressPerformingMembers.label ?? "").trim()) && (
            <div className="px-5 py-4 flex items-center justify-between gap-3">
              <p className="text-sm text-white font-medium truncate min-w-0">
                {(expressPerformingMembers.label ?? "").trim() || "—"}
              </p>
              <div className="shrink-0 text-[15px] font-semibold text-white pr-1">
                {expressPerformingMembers.count ?? membersCount}
              </div>
            </div>
          )}

          {/* <div className="p-5 space-y-3">
            {allOptions.length === 0 ? (
              <p className="text-[#8E8E93] text-sm py-2">
                No members in your profile yet. Add one below.
              </p>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {allOptions.map((m) => (
                  <li
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      handleToggleMember(m.id, !selectedMemberIds.has(m.id))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleToggleMember(m.id, !selectedMemberIds.has(m.id));
                      }
                    }}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-xl border min-h-[52px] cursor-pointer transition-colors",
                      selectedMemberIds.has(m.id)
                        ? "border-[#F33363]/40 bg-[#F33363]/5"
                        : "border-[#2E2E32] bg-[#1C1C1E]/80",
                    )}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="shrink-0"
                    >
                      <Checkbox
                        checked={selectedMemberIds.has(m.id)}
                        onCheckedChange={(c) =>
                          handleToggleMember(m.id, c === true)
                        }
                      />
                    </div>
                    <div className="flex-1 items-center gap-2 min-w-0">
                      <p className="text-white font-medium truncate">
                        {m.name}
                      </p>
                      <p className="text-[#8E8E93] text-xs truncate">
                        {m.role}
                        {m.age ? ` · ${m.age}` : ""}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {membersCount > 0 && allOptions.length < membersCount && (
              <p className="text-amber-500/90 text-[13px]">
                Add {membersCount - allOptions.length} more member(s) below.
              </p>
            )}
            {!showAddMemberForm && (
              <button
                type="button"
                onClick={handleOpenAddMember}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-[#2E2E32] text-[#8E8E93] hover:text-white hover:border-[#4A4A4E] hover:bg-[#1C1C1E]/80 transition-colors min-h-[44px]"
              >
                <Plus className="h-4 w-4" /> Add member (saves to profile)
              </button>
            )}
            {showAddMemberForm && (
              <div className="p-4 rounded-2xl bg-[#1C1C1E] border border-[#2E2E32] space-y-4">
                <button
                  type="button"
                  onClick={handleAddMoreDraftMembers}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" /> Add more members
                </button>
                {draftNewMembers.map((member, index) => {
                  const prefix = `draft-${member.id}`;
                  return (
                    <div
                      key={member.id}
                      className="rounded-xl border border-[#2E2E32] p-3 space-y-3 bg-[#121214]"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-[#8E8E93]">
                          New member {index + 1}
                        </span>
                        {draftNewMembers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveDraftMember(member.id)}
                            className="p-1.5 rounded-lg text-[#8E8E93] hover:text-red-400 hover:bg-red-500/10"
                            aria-label="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <Input
                        label="Name"
                        value={member.name}
                        onChange={(e) =>
                          handleDraftMemberChange(
                            member.id,
                            "name",
                            e.target.value,
                          )
                        }
                        placeholder="Full name"
                        className={inputBaseClass}
                        error={addMemberErrors[`${prefix}-name`]}
                      />
                      <Input
                        label="Role"
                        value={member.role}
                        onChange={(e) =>
                          handleDraftMemberChange(
                            member.id,
                            "role",
                            e.target.value,
                          )
                        }
                        placeholder="e.g. Vocalist"
                        className={inputBaseClass}
                        error={addMemberErrors[`${prefix}-role`]}
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="number"
                          label="Age"
                          value={member.age}
                          onChange={(e) =>
                            handleDraftMemberChange(
                              member.id,
                              "age",
                              e.target.value.replace(/\D/g, "").slice(0, 2),
                            )
                          }
                          placeholder="0"
                          inputMode="numeric"
                          className={inputBaseClass}
                          error={addMemberErrors[`${prefix}-age`]}
                        />
                        <div>
                          <label className="block text-xs font-medium text-[#8E8E93] mb-1">
                            Gender
                          </label>
                          <select
                            value={member.gender}
                            onChange={(e) =>
                              handleDraftMemberChange(
                                member.id,
                                "gender",
                                e.target.value,
                              )
                            }
                            className={inputBaseClass}
                          >
                            <option value="">Select</option>
                            {GENDER_OPTIONS.map((g) => (
                              <option key={g.value} value={g.value}>
                                {g.label}
                              </option>
                            ))}
                          </select>
                          {addMemberErrors[`${prefix}-gender`] && (
                            <p className="text-red-400 text-xs mt-1">
                              {addMemberErrors[`${prefix}-gender`]}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    variant="active"
                    onClick={() => void handleSaveAddMembers()}
                    className="flex-1 min-h-[44px]"
                    disabled={saveLoadingPerformance}
                  >
                    {saveLoadingPerformance ? (
                      <Loader className="h-5 w-5 animate-spin" />
                    ) : (
                      "Save to profile"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCancelAddMembers}
                    className="flex-1 min-h-[44px]"
                    disabled={saveLoadingPerformance}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div> */}
        </section>

        {/* Tech rider & TBL */}
        <section className="rounded-[2rem] bg-[#151517] border border-[#2E2E32] overflow-hidden">
          <div className="p-5 border-b border-[#2E2E32]">
            <h2 className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider flex items-center gap-2">
              <FileText className="h-4 w-4" /> Tech rider & travel
            </h2>
          </div>
          <div className="p-5 space-y-5">
            <div>
              <p className="text-[#8E8E93] text-xs font-medium mb-1">
                Tech rider
              </p>
              {techRiderEnabled ? (
                <>
                  <p className="text-emerald-500/90 text-[13px] font-medium mb-2">
                    Applicable
                  </p>
                  <textarea
                    value={techRiderValue}
                    onChange={(e) => {
                      setTechRiderValue(e.target.value);
                      setValidationError(null);
                    }}
                    placeholder="Enter tech rider details"
                    className={cn(
                      inputBaseClass,
                      "min-h-[100px] resize-y py-3",
                    )}
                    rows={3}
                  />
                </>
              ) : (
                <p className="text-[#8E8E93] text-sm">
                  {expressSoundSetupAdded === true
                    ? "Sound setup included in your fee. Artist must carry 1 mic with stand, 1 mixer, 2 speakers and 2 AUX cables."
                    : "Not required for this gathering size."}
                </p>
              )}
            </div>
            <div>
              <p className="text-[#8E8E93] text-xs font-medium mb-1">
                Travel, boarding & lodging
              </p>
              {tblEnabled ? (
                <>
                  <p className="text-emerald-500/90 text-[13px] font-medium mb-2">
                    Applicable
                  </p>
                  <textarea
                    value={travelBoardingLodging}
                    onChange={(e) => {
                      setTravelBoardingLodging(e.target.value);
                      setValidationError(null);
                    }}
                    placeholder="Enter requirements"
                    className={cn(
                      inputBaseClass,
                      "min-h-[100px] resize-y py-3",
                    )}
                    rows={3}
                  />
                </>
              ) : (
                <p className="text-[#8E8E93] text-sm">Included in your fee</p>
              )}
            </div>
          </div>
        </section>

        {/* Bank account */}
        <section className="rounded-[2rem] bg-[#151517] border border-[#2E2E32] overflow-hidden">
          <div className="p-5 border-b border-[#2E2E32]">
            <h2 className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Bank account
            </h2>
            <p className="text-white/80 text-sm mt-1">
              Choose where to receive payment
            </p>
          </div>
          <div className="p-5 space-y-4">
            {banks.length === 0 ? (
              <div className="py-6 text-center rounded-2xl bg-amber-500/5 border border-amber-500/20">
                <p className="text-amber-500/90 text-sm font-medium mb-1">
                  No bank accounts yet
                </p>
                <p className="text-[#8E8E93] text-[13px] mb-4">
                  Add one in Event Essentials → Billing Details.
                </p>
                <div className="flex justify-center w-full">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      navigate("/profile/event-essentials?tab=billing")
                    }
                    className="min-h-[44px] w-full max-w-xs sm:w-auto"
                  >
                    Go to Billing
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <label className="block text-sm font-medium text-white">
                  Account
                </label>
                <select
                  value={selectedBankId ?? ""}
                  onChange={(e) => {
                    setSelectedBankId(
                      e.target.value ? Number(e.target.value) : null,
                    );
                    setValidationError(null);
                  }}
                  className={inputBaseClass}
                >
                  <option value="">Choose account</option>
                  {banks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.bank_name} · ****{b.account_number.slice(-4)}
                    </option>
                  ))}
                </select>
                {selectedBankId != null && bankFieldsFromSelection && (
                  <div className="p-4 rounded-2xl bg-[#0B0B0D] border border-[#3A3A3C] space-y-2 text-[13px]">
                    <div className="flex justify-between">
                      <span className="text-[#8E8E93]">Bank</span>
                      <span className="text-white font-medium">
                        {bankFieldsFromSelection.bank_name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8E8E93]">Branch Name</span>
                      <span className="text-white font-medium">
                        {bankFieldsFromSelection.branch}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8E8E93]">Account Holder</span>
                      <span className="text-white font-medium">
                        {bankFieldsFromSelection.account_holder_name}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* GST & PAN */}
        <section className="rounded-[2rem] bg-[#151517] border border-[#2E2E32] overflow-hidden">
          <div className="p-5 border-b border-[#2E2E32]">
            <h2 className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider flex items-center gap-2">
              <Banknote className="h-4 w-4" /> PAN
            </h2>
          </div>
          <div className="p-5 space-y-4">
            {!hasPan && (
              <div className="py-3 px-4 rounded-xl bg-amber-500/5 border border-amber-500/20 mb-2">
                <p className="text-amber-500/90 text-[13px]">
                  Add PAN in Billing Details to reuse across bookings.
                </p>
                <button
                  type="button"
                  onClick={() =>
                    navigate("/profile/event-essentials?tab=billing")
                  }
                  className="text-[#F33363] text-[13px] font-medium mt-1"
                >
                  Go to Billing →
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="PAN (required)"
                value={pan}
                onChange={(e) => {
                  const nextPan = e.target.value.toUpperCase();
                  setPan(nextPan);
                  if (nextPan && !REGEX_PAN.test(nextPan)) {
                    setPanError("Invalid PAN format");
                  } else {
                    setPanError("");
                  }
                  setValidationError(null);
                }}
                placeholder="e.g. ABCDExxxxx"
                maxLength={10}
                className={inputBaseClass}
                error={panError}
              />
              {/* <Input
                label="GSTIN (optional)"
                value={gstin}
                onChange={(e) => {
                  setGstin(e.target.value.toUpperCase());
                  setValidationError(null);
                }}
                placeholder="GSTIN"
                className={inputBaseClass}
              /> */}
            </div>
          </div>
        </section>

        {/* Terms */}
        <section className="rounded-[2rem] bg-[#151517] border border-[#2E2E32] overflow-hidden">
          <div className="p-5 border-b border-[#2E2E32]">
            <h2 className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider flex items-center gap-2">
              <FileText className="h-4 w-4" /> Terms
            </h2>
          </div>
          <div className="p-5 space-y-4">
            {/* <p className="text-[#E5E5EA]/60 text-[13px] leading-relaxed">
              {TERMS_TEXT}
            </p> */}
            <button
              type="button"
              onClick={() => setAnnexureOpen(!annexureOpen)}
              className="flex items-center gap-2 text-[#F33363] font-medium text-[13px] min-h-[44px]"
            >
              {annexureOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              Express — Booking Terms and Conditions
            </button>
            {annexureOpen && (
              <div className="mt-3 p-4 rounded-2xl bg-[#0B0B0D] border border-[#3A3A3C] max-h-[50vh] overflow-y-auto">
                <AnnexureAContent />
              </div>
            )}
            <div className="pt-2">
              <Checkbox
                checked={termsAccepted}
                onCheckedChange={(c) => {
                  setTermsAccepted(c === true);
                  setValidationError(null);
                }}
                label="I accept the Express Booking Terms and Conditions and confirm the above."
              />
            </div>
          </div>
        </section>

        <Button
          type="button"
          onClick={handleConfirmBookingClick}
          disabled={submitLoading}
          variant="active"
          className="w-full py-4 rounded-2xl font-bold text-base min-h-[52px] bg-[#F33363] hover:bg-[#FF5E8E] text-white shadow-lg active:scale-[0.99]"
        >
          {submitLoading ? (
            <Loader className="h-5 w-5 animate-spin" />
          ) : (
            "Confirm Booking"
          )}
        </Button>
      </div>

      {/* Confirm modal */}
      {preConfirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="rounded-2xl border border-[#2E2E32] shadow-xl w-full max-w-md overflow-hidden bg-[#151517]">
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-[20px] font-semibold text-white truncate">
                    Almost There!
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setPreConfirmModalOpen(false)}
                  className="shrink-0 h-10 w-10 rounded-full border border-[#F33363] text-white hover:bg-[#F33363]/10 transition-colors flex items-center justify-center"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
                <div
                  role="button"
                  tabIndex={0}
                  className="flex items-start gap-3"
                  onClick={() =>
                    setPreConfirmChecks((p) => ({
                      ...p,
                      report15MinEarly: !p.report15MinEarly,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setPreConfirmChecks((p) => ({
                        ...p,
                        report15MinEarly: !p.report15MinEarly,
                      }));
                    }
                  }}
                >
                  <div
                    className="shrink-0 mt-0.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={preConfirmChecks.report15MinEarly}
                      onCheckedChange={(c) =>
                        setPreConfirmChecks((p) => ({
                          ...p,
                          report15MinEarly: c === true,
                        }))
                      }
                      checkboxClassName="rounded-[4px] bg-transparent border-[#F33363] data-[state=checked]:bg-[#F33363] data-[state=checked]:border-[#F33363]"
                    />
                  </div>
                  <p className="text-[13px] text-white/90 leading-relaxed">
                    I confirm that I will report to the venue at least 15 minutes
                    before the scheduled start time. I understand that failure
                    to do so will lead to loss of access to future StarClinch
                    jobs.
                  </p>
                </div>

                {expressSoundSetupAdded === true && (
                  <div
                    role="button"
                    tabIndex={0}
                    className="flex items-start gap-3"
                    onClick={() =>
                      setPreConfirmChecks((p) => ({
                        ...p,
                        soundSetupIncluded: !p.soundSetupIncluded,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setPreConfirmChecks((p) => ({
                          ...p,
                          soundSetupIncluded: !p.soundSetupIncluded,
                        }));
                      }
                    }}
                  >
                    <div
                    className="shrink-0 mt-0.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                      <Checkbox
                        checked={preConfirmChecks.soundSetupIncluded}
                        onCheckedChange={(c) =>
                          setPreConfirmChecks((p) => ({
                            ...p,
                            soundSetupIncluded: c === true,
                          }))
                        }
                        checkboxClassName="rounded-[4px] bg-transparent border-[#F33363] data-[state=checked]:bg-[#F33363] data-[state=checked]:border-[#F33363]"
                      />
                    </div>
                    <p className="text-[13px] text-white/90 leading-relaxed">
                      I confirm that my fee is inclusive of the sound setup
                      cost, and I will carry the complete setup, including 1 mic
                      with stand, 1 mixer, 2 speakers, and 2 AUX cables.
                    </p>
                  </div>
                )}

                {expressOfferKind != null && (
                  <div
                    role="button"
                    tabIndex={0}
                    className="flex items-start gap-3"
                    onClick={() =>
                      setPreConfirmChecks((p) => ({
                        ...p,
                        bringInstrumentalist: !p.bringInstrumentalist,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setPreConfirmChecks((p) => ({
                          ...p,
                          bringInstrumentalist: !p.bringInstrumentalist,
                        }));
                      }
                    }}
                  >
                    <div
                    className="shrink-0 mt-0.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                      <Checkbox
                        checked={preConfirmChecks.bringInstrumentalist}
                        onCheckedChange={(c) =>
                          setPreConfirmChecks((p) => ({
                            ...p,
                            bringInstrumentalist: c === true,
                          }))
                        }
                        checkboxClassName="rounded-[4px] bg-transparent border-[#F33363] data-[state=checked]:bg-[#F33363] data-[state=checked]:border-[#F33363]"
                      />
                    </div>
                    <p className="text-[13px] text-white/90 leading-relaxed">
                      I confirm that I will bring the required instrumentalist
                      along with me for this performance.
                    </p>
                  </div>
                )}

                <div
                  role="button"
                  tabIndex={0}
                  className="flex items-start gap-3"
                  onClick={() =>
                    setPreConfirmChecks((p) => ({
                      ...p,
                      collectOtp: !p.collectOtp,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setPreConfirmChecks((p) => ({
                        ...p,
                        collectOtp: !p.collectOtp,
                      }));
                    }
                  }}
                >
                  <div
                    className="shrink-0 mt-0.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={preConfirmChecks.collectOtp}
                      onCheckedChange={(c) =>
                        setPreConfirmChecks((p) => ({
                          ...p,
                          collectOtp: c === true,
                        }))
                      }
                      checkboxClassName="rounded-[4px] bg-transparent border-[#F33363] data-[state=checked]:bg-[#F33363] data-[state=checked]:border-[#F33363]"
                    />
                  </div>
                  <p className="text-[13px] text-white/90 leading-relaxed">
                    I confirm that I will collect and enter the OTP received
                    from the customer before starting my performance. I
                    understand that failure to enter the OTP will result in
                    payment not being processed, and StarClinch will not be
                    liable for the same.
                  </p>
                </div>
              </div>

              <Button
                type="button"
                onClick={handlePreConfirmProceed}
                disabled={!isPreConfirmComplete}
                variant="active"
                className={cn(
                  "w-full py-4 rounded-2xl font-bold text-base min-h-[52px] shadow-lg active:scale-[0.99]",
                  isPreConfirmComplete
                    ? "bg-[#F33363] hover:bg-[#FF5E8E] text-white"
                    : "bg-[#F33363]/35 text-white/40 cursor-not-allowed",
                )}
              >
                I Confirm &amp; Proceed
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="rounded-2xl border border-[#3A3A3C]/50 shadow-xl w-full max-w-sm overflow-hidden bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)]">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-bold text-white">Confirm booking?</h3>
              <p className="text-[#8E8E93] text-sm">
                You’re about to submit this form. This can’t be undone.
              </p>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1 min-h-[48px]"
                  onClick={() => setConfirmModalOpen(false)}
                  disabled={submitLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="active"
                  className="flex-1 min-h-[48px] bg-[#F33363] hover:bg-[#FF5E8E]"
                  onClick={() => void handleConfirmSubmit()}
                  disabled={submitLoading}
                >
                  {submitLoading ? (
                    <Loader className="h-5 w-5 animate-spin" />
                  ) : (
                    "Confirm"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
